import logger from '../../utils/logger';
import React, { useState, useRef, useEffect, useContext } from 'react';
import { Upload, Download, FileText, CheckCircle, XCircle, Loader, File, AlertCircle, MoreVertical } from 'lucide-react';
import "../../style/GCSDocumentManager.css";
import { uploadOrderToBucket, getUserId } from "../../utils/utils"
import { GlobalStateContext } from "../../utils/GlobalStateContext";
import PDFModalDocumentsViewer from '../../components/PDFModalDocumentsViewer';

const GCSDocumentManager = ({ onPendingDocumentsChange, orderId, orderType }) => {
  const [file, setFile] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [documents, setDocuments] = useState([]);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [modalState, setModalState] = useState({ show: false, doc_id: null });
  const [sessionId] = useState(() => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  // Toggle states for order-level vs patient-level
  const [viewMode, setViewMode] = useState(orderId ? 'order' : 'all'); // 'order' or 'all'
  const [uploadLinkMode, setUploadLinkMode] = useState(orderId ? 'order' : 'patient'); // 'order' or 'patient'

  const fileInputRef = useRef(null);
  const { currentPatient } = useContext(GlobalStateContext);
  const { currentUser } = useContext(GlobalStateContext);
  const user = getUserId(currentUser);
  const ris_patients_id = currentPatient?.ris_patients_id || null;
  const isPendingPatient = !ris_patients_id;
  const BASE_URL = process.env.REACT_APP_API_URL;
  const hasOrderContext = !!(orderId && orderType);

  // Reassign state
  const [reassignDocId, setReassignDocId] = useState(null);
  const [reassignOrderId, setReassignOrderId] = useState('');
  const [reassignOrderType, setReassignOrderType] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignMessage, setReassignMessage] = useState(null); // { type: 'success'|'error', text: '' }
  const reassignTimerRef = useRef(null);

  const showReassignMessage = (type, text) => {
    setReassignMessage({ type, text });
    if (reassignTimerRef.current) clearTimeout(reassignTimerRef.current);
    reassignTimerRef.current = setTimeout(() => setReassignMessage(null), 4000);
  };

  const handleReassign = async (docId, overrideOrderId, overrideOrderType) => {
    const targetOrderId = overrideOrderId !== undefined ? overrideOrderId : reassignOrderId;
    const targetOrderType = overrideOrderType !== undefined ? overrideOrderType : reassignOrderType;
    setReassignLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/upload/orders/${docId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: targetOrderId || null,
          order_type: targetOrderId ? targetOrderType : null,
          user,
          type: currentUser.type,
          ris_patients_id
        }),
      });
      if (!response.ok) throw new Error('Failed to reassign document');
      setReassignDocId(null);
      setReassignOrderId('');
      setReassignOrderType('');
      if (targetOrderId) {
        showReassignMessage('success', `Document linked to ${targetOrderType} order #${targetOrderId}`);
      } else {
        showReassignMessage('success', 'Document unlinked from order');
      }
      await fetchDocuments();
    } catch (error) {
      logger.error('Reassign error:', error);
      showReassignMessage('error', 'Failed to reassign document. Please try again.');
    } finally {
      setReassignLoading(false);
    }
  };

  const openReassign = (doc) => {
    setReassignDocId(doc.id);
    setReassignOrderId(doc.order_id || '');
    setReassignOrderType(doc.order_type || orderType || 'Radiology');
  };

  const closeReassign = () => {
    setReassignDocId(null);
    setReassignOrderId('');
    setReassignOrderType('');
  };

  // Fetch list of documents on component mount and when view mode changes
  useEffect(() => {
    if (!isPendingPatient) {
      fetchDocuments();
    } else {
      loadPendingDocuments();
    }
  }, [isPendingPatient, viewMode, orderId]);

  // Notify parent component when pending documents change
  useEffect(() => {
    if (isPendingPatient && onPendingDocumentsChange) {
      onPendingDocumentsChange(pendingDocuments, sessionId);
    }
  }, [pendingDocuments, isPendingPatient, onPendingDocumentsChange, sessionId]);

  // Reset view mode when orderId changes
  useEffect(() => {
    setViewMode(orderId ? 'order' : 'all');
    setUploadLinkMode(orderId ? 'order' : 'patient');
  }, [orderId]);

  const loadPendingDocuments = () => {
    try {
      const stored = sessionStorage.getItem(`pending_docs_${sessionId}`);
      if (stored) {
        setPendingDocuments(JSON.parse(stored));
      }
    } catch (error) {
      logger.error('Failed to load pending documents:', error);
    }
  };

  const savePendingDocuments = (docs) => {
    try {
      sessionStorage.setItem(`pending_docs_${sessionId}`, JSON.stringify(docs));
    } catch (error) {
      logger.error('Failed to save pending documents:', error);
    }
  };

  const fetchDocuments = async () => {
    if (isPendingPatient) {
      loadPendingDocuments();
      return;
    }

    setIsLoadingDocs(true);
    try {
      let url = `${BASE_URL}/upload/orders?ris_patients_id=${ris_patients_id}&user=${user}&type=${currentUser.type}`;
      if (viewMode === 'order' && hasOrderContext) {
        url += `&order_id=${orderId}&order_type=${orderType}`;
      }
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }
      const data = await response.json();
      setDocuments(data);
    } catch (error) {
      logger.error('Failed to fetch documents:', error);
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setUploadError('');
    }
  };

  const handleClick = (doc_id) => {
    setModalState({ show: true, doc_id: doc_id });
  };

  const uploadToGCP = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadError('');

    try {
      if (isPendingPatient) {
        const pendingDoc = {
          id: `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          file_name: file.name,
          size: file.size,
          uploaded_at: new Date().toISOString(),
          session_id: sessionId,
          status: 'pending',
          fileData: await fileToBase64(file),
          fileType: file.type
        };

        const updatedPendingDocs = [...pendingDocuments, pendingDoc];
        setPendingDocuments(updatedPendingDocs);
        savePendingDocuments(updatedPendingDocs);

        setUploadedUrl(file.name);
        setFile(null);
        setShowUploadDialog(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        const bodyPayload = {
          file_name: file.name,
          ris_patients_id: ris_patients_id,
          size: file.size,
          user: user,
          type: currentUser.type
        };

        // Link to specific order if toggle is set
        if (uploadLinkMode === 'order' && hasOrderContext) {
          bodyPayload.order_id = orderId;
          bodyPayload.order_type = orderType;
        }

        const documentResponse = await fetch(`${BASE_URL}/upload/orders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bodyPayload),
        });

        if (!documentResponse.ok) {
          throw new Error('Failed to save document metadata');
        }

        const documentId = await documentResponse.json();

        const uploadBucketResponse = await uploadOrderToBucket(file, documentId, currentUser.type, user)

        if (!uploadBucketResponse) {
          throw new Error('Failed to upload document to bucket.');
        }

        setUploadedUrl(file.name);
        setFile(null);
        setShowUploadDialog(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        await fetchDocuments();
      }
    } catch (error) {
      logger.error('Upload failed:', error);
      setUploadError(error.message || 'Failed to upload document. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const resetUpload = () => {
    setShowUploadDialog(false);
    setFile(null);
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetDownload = () => {
    setShowDownloadDialog(false);
    setDownloadUrl('');
    setDownloadError('');
    setSelectedDoc(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderLabel = (doc) => {
    if (doc.order_id && doc.order_type) {
      return `${doc.order_type} #${doc.order_id}`;
    }
    return 'Patient';
  };

  return (
    <div className="gcs-container">
      {isPendingPatient && (
        <div className="gcs-alert gcs-alert-warning">
          <AlertCircle className="gcs-alert-icon" />
          <div className="gcs-alert-content">
            <p className="gcs-alert-title">Pending Patient Mode</p>
            <p className="gcs-alert-text">
              Documents will be queued and uploaded after the patient is created in the system.
            </p>
          </div>
        </div>
      )}

      {uploadedUrl && (
        <div className="gcs-alert gcs-alert-success">
          <CheckCircle className="gcs-alert-icon" />
          <div className="gcs-alert-content">
            <p className="gcs-alert-title">
              {isPendingPatient ? 'Document queued successfully' : 'Document uploaded successfully'}
            </p>
            <p className="gcs-alert-text">{uploadedUrl}</p>
          </div>
          <button
            type="button"
            onClick={() => setUploadedUrl('')}
            className="gcs-alert-close"
            aria-label="Dismiss"
          >
            <XCircle />
          </button>
        </div>
      )}

      <div className="gcs-grid">
        {/* Upload Section */}
        <div className="gcs-card">
          <div className="gcs-card-header">
            <Upload className="gcs-card-icon" />
            <h3 className="gcs-card-title">Upload Document</h3>
          </div>

          {!showUploadDialog ? (
            <button
              type="button"
              onClick={() => setShowUploadDialog(true)}
              className="gcs-button gcs-button-primary gcs-button-full"
            >
              Upload New Document
            </button>
          ) : (
            <div className="gcs-form">
              {/* Upload link toggle */}
              {hasOrderContext && !isPendingPatient && (
                <div className="gcs-link-toggle">
                  <span className="gcs-link-toggle-label">Link to:</span>
                  <div className="gcs-toggle-group">
                    <button
                      type="button"
                      className={`gcs-toggle-btn ${uploadLinkMode === 'order' ? 'gcs-toggle-active' : ''}`}
                      onClick={() => setUploadLinkMode('order')}
                    >
                      This Order
                    </button>
                    <button
                      type="button"
                      className={`gcs-toggle-btn ${uploadLinkMode === 'patient' ? 'gcs-toggle-active' : ''}`}
                      onClick={() => setUploadLinkMode('patient')}
                    >
                      Patient Only
                    </button>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="gcs-file-input"
              />

              {file && (
                <div className="gcs-file-preview">
                  <p className="gcs-file-label">Selected file:</p>
                  <p className="gcs-file-name">{file.name}</p>
                  <p className="gcs-file-size">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              )}

              {uploadError && (
                <div className="gcs-alert gcs-alert-error">
                  <XCircle className="gcs-alert-icon-small" />
                  <p className="gcs-alert-text">{uploadError}</p>
                </div>
              )}

              <div className="gcs-button-group">
                <button
                  type="button"
                  onClick={uploadToGCP}
                  disabled={!file || isUploading}
                  className="gcs-button gcs-button-primary gcs-button-flex"
                >
                  {isUploading ? (
                    <>
                      <Loader className="gcs-spinner" />
                      Uploading...
                    </>
                  ) : (
                    'Upload'
                  )}
                </button>
                <button
                  type="button"
                  onClick={resetUpload}
                  disabled={isUploading}
                  className="gcs-button gcs-button-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Download Section */}
        <div className="gcs-card">
          <div className="gcs-card-header">
            <Download className="gcs-card-icon" />
            <h3 className="gcs-card-title">Retrieve Document</h3>
          </div>

          {!showDownloadDialog ? (
            <button
              type="button"
              onClick={() => setShowDownloadDialog(true)}
              className="gcs-button gcs-button-dark gcs-button-full"
            >
              View Documents
            </button>
          ) : (
            <div className="gcs-form">
              {/* View mode toggle */}
              {hasOrderContext && !isPendingPatient && (
                <div className="gcs-link-toggle">
                  <span className="gcs-link-toggle-label">Showing:</span>
                  <div className="gcs-toggle-group">
                    <button
                      type="button"
                      className={`gcs-toggle-btn ${viewMode === 'order' ? 'gcs-toggle-active' : ''}`}
                      onClick={() => setViewMode('order')}
                    >
                      This Order
                    </button>
                    <button
                      type="button"
                      className={`gcs-toggle-btn ${viewMode === 'all' ? 'gcs-toggle-active' : ''}`}
                      onClick={() => setViewMode('all')}
                    >
                      All Documents
                    </button>
                  </div>
                </div>
              )}

              {reassignMessage && (
                <div className={`gcs-alert ${reassignMessage.type === 'success' ? 'gcs-alert-success' : 'gcs-alert-error'}`}>
                  {reassignMessage.type === 'success'
                    ? <CheckCircle className="gcs-alert-icon" />
                    : <XCircle className="gcs-alert-icon-small" />
                  }
                  <p className="gcs-alert-text" style={{ color: reassignMessage.type === 'success' ? '#166534' : '#991b1b' }}>
                    {reassignMessage.text}
                  </p>
                  <button type="button" onClick={() => setReassignMessage(null)} className="gcs-alert-close" style={{ color: reassignMessage.type === 'success' ? '#16a34a' : '#991b1b' }}>
                    <XCircle size={14} />
                  </button>
                </div>
              )}

              {isLoadingDocs ? (
                <div className="gcs-loading">
                  <Loader className="gcs-spinner" />
                  <p>Loading documents...</p>
                </div>
              ) : isPendingPatient ? (
                pendingDocuments.length === 0 ? (
                  <div className="gcs-empty">
                    <File className="gcs-empty-icon" />
                    <p className="gcs-empty-text">No pending documents</p>
                  </div>
                ) : (
                  <div className="gcs-document-list">
                    {pendingDocuments.map((doc) => (
                      <div
                        key={doc.id}
                        className="gcs-document-item gcs-document-pending"
                      >
                        <File className="gcs-document-icon" />
                        <div className="gcs-document-info">
                          <p className="gcs-document-name">{doc.file_name}</p>
                          <p className="gcs-document-meta">
                            {formatFileSize(doc.size)} • Pending Upload
                          </p>
                        </div>
                        <span className="gcs-pending-badge">Queued</span>
                      </div>
                    ))}
                  </div>
                )
              ) : documents.length === 0 ? (
                <div className="gcs-empty">
                  <File className="gcs-empty-icon" />
                  <p className="gcs-empty-text">
                    {viewMode === 'order' ? 'No documents for this order' : 'No documents available'}
                  </p>
                </div>
              ) : (
                <div className="gcs-document-list">
                  {documents.map((doc) => (
                    <div key={doc.id} style={{ position: 'relative' }}>
                      <div className="gcs-document-item" style={{ cursor: 'default' }}>
                        <button
                          type="button"
                          onClick={() => handleClick(doc.id)}
                          disabled={isFetching && selectedDoc?.id === doc.id}
                          style={{ display: 'contents', cursor: 'pointer' }}
                        >
                          <File className="gcs-document-icon" />
                          <div className="gcs-document-info">
                            <p className="gcs-document-name">{doc.file_name}</p>
                            <p className="gcs-document-meta">
                              {formatFileSize(doc.size)} • {formatDate(doc.uploaded_at)}
                            </p>
                          </div>
                        </button>
                        {viewMode === 'all' && (
                          <span className={doc.order_id ? 'gcs-order-badge' : 'gcs-patient-badge'}>
                            {getOrderLabel(doc)}
                          </span>
                        )}
                        {isFetching && selectedDoc?.id === doc.id && (
                          <Loader className="gcs-spinner-small" />
                        )}
                        {!isPendingPatient && (
                          <div className="gcs-document-actions">
                            <button
                              type="button"
                              className="gcs-action-btn"
                              title="Reassign document"
                              onClick={(e) => {
                                e.stopPropagation();
                                reassignDocId === doc.id ? closeReassign() : openReassign(doc);
                              }}
                            >
                              <MoreVertical size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                      {reassignDocId === doc.id && (
                        <div className="gcs-reassign-dropdown" onClick={(e) => e.stopPropagation()}>
                          <div className="gcs-reassign-title">Reassign Document</div>
                          {/* Quick actions */}
                          {hasOrderContext && doc.order_id !== orderId && (
                            <div className="gcs-reassign-field">
                              <button
                                type="button"
                                className="gcs-reassign-save"
                                style={{ width: '100%' }}
                                disabled={reassignLoading}
                                onClick={() => handleReassign(doc.id, orderId, orderType)}
                              >
                                Link to Current Order ({orderType} #{orderId})
                              </button>
                            </div>
                          )}
                          {doc.order_id && (
                            <div className="gcs-reassign-field">
                              <button
                                type="button"
                                className="gcs-reassign-cancel"
                                style={{ width: '100%' }}
                                disabled={reassignLoading}
                                onClick={() => handleReassign(doc.id, '', '')}
                              >
                                Unlink from Order (Patient Only)
                              </button>
                            </div>
                          )}
                          <div className="gcs-reassign-field" style={{ marginTop: 4 }}>
                            <label>Order Type</label>
                            <select
                              value={reassignOrderType}
                              onChange={(e) => setReassignOrderType(e.target.value)}
                            >
                              <option value="Radiology">Radiology</option>
                              <option value="Lab">Lab</option>
                            </select>
                          </div>
                          <div className="gcs-reassign-field">
                            <label>Order ID (leave blank for patient-only)</label>
                            <input
                              type="number"
                              value={reassignOrderId}
                              onChange={(e) => setReassignOrderId(e.target.value)}
                              placeholder="e.g. 42"
                            />
                          </div>
                          <div className="gcs-reassign-actions">
                            <button
                              type="button"
                              className="gcs-reassign-save"
                              disabled={reassignLoading}
                              onClick={() => handleReassign(doc.id)}
                            >
                              {reassignLoading ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              className="gcs-reassign-cancel"
                              onClick={closeReassign}
                              disabled={reassignLoading}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {downloadError && (
                <div className="gcs-alert gcs-alert-error">
                  <XCircle className="gcs-alert-icon-small" />
                  <p className="gcs-alert-text">{downloadError}</p>
                </div>
              )}

              <button
                type="button"
                onClick={resetDownload}
                disabled={isFetching}
                className="gcs-button gcs-button-secondary gcs-button-full"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    {modalState.show && (
      <PDFModalDocumentsViewer
          document_id={modalState.doc_id}
          onClose={() => setModalState({ show: false })}
      />
    )}
    </div>
  );
};

export default GCSDocumentManager;
